import { makeAutoObservable } from 'mobx';
import { token } from '@/infrastructure/container';
import {
  DomainError,
  ForbiddenError,
  NotFoundError,
  OfflineError,
  ServerError,
  UpdateRequiredError,
} from '@/domain/errors';
import type { ScheduledSession } from '@/domain/project';
import { buildSessionCsv, csvFilename } from '@/domain/report';
import type { IReportGateway } from '@/domain/reportGateway';
import { sessionUntouched } from '@/domain/session';
import { CSV_MIME, type IDomDownload } from '@/services/DomDownload';
import type { ILogger } from '@/services/Logger';

// The admin's report ViewModel: pick a session off the day's run list, get one CSV back. That file
// is the entire handover to the humans who pick teams (docs/reporting.md), which is why a failure
// here is a sentence on the screen and never a silent nothing.

// What one export produces. The filename is
// `<session name, lowercase, spaces->dashes>-<sessionId first 8 chars>.csv`; the csv is
// `buildSessionCsv` over the state the gateway loaded.
export interface ExportedCsv {
  readonly filename: string;
  readonly csv: string;
}

// The one sentence with two callers. The gateway that rejects with a `NotFoundError` gets it, and
// so does the fold that comes back untouched, which is what the real gateway does for a session
// nobody ever started (see `sessionUntouched`).
const NEVER_RUN = 'That session was never run, so there is nothing to export.';

export class ReportStore {
  private _busy = false;
  private _lastError: string | undefined;
  // The refusal is kept apart from the failure because it belongs to a DIFFERENT export from the
  // one that is running: the second click's problem, cleared when the first click's export settles.
  // Left on `_lastError` it would sit on the screen next to a file that downloaded fine.
  private _refusal: string | undefined;

  constructor(
    private readonly gateway: IReportGateway,
    private readonly logger: ILogger,
    // Appended and optional, because the contract pins the two-argument shape and its blind oracle
    // constructs it that way. The oracle's subject is the STRING; handing the string to the browser
    // is this store's other half, and the wrapper is what keeps `document` out of the view
    // (INVARIANTS rule 12). A build without it can still export, it just cannot save.
    private readonly download?: IDomDownload,
  ) {
    makeAutoObservable(this);
  }

  // The day's run list, straight off the project fold. The picker renders this, and it is a live
  // read rather than a snapshot: a session scheduled on another device appears without a reload.
  get sessions(): readonly ScheduledSession[] {
    return this.gateway.projectState.sessions;
  }

  // One export at a time. A second while the first is in flight is refused, not queued.
  get busy(): boolean {
    return this._busy;
  }

  // Why the last export did not produce a file, in words an admin can act on. One line for the
  // screen, whichever of the two reasons is standing: a refusal while an export is in flight, or
  // the failure the last finished export left behind.
  get lastError(): string | undefined {
    return this._refusal ?? this._lastError;
  }

  // Never rejects. The outcome is the resolved value or `lastError`, and one of the two is always
  // true after this settles (INVARIANTS rule 17: nothing rejects out of a store into a view).
  exportSession = async (sessionId: string): Promise<ExportedCsv | undefined> => {
    // Refused rather than queued: two files racing to the same download slot, and the second one
    // folds a whole aggregate to produce a file nobody asked twice for. Visible, unlike
    // AdminAccessStore's equivalent, because there is no disabled button standing in front of it —
    // the run list has one Export per session and the admin's second click lands on a different
    // row from the first.
    if (this._busy) {
      this._refusal = 'An export is already running. Wait for it to finish.';
      return undefined;
    }
    this.startExport();
    try {
      const state = await this.gateway.loadSession(sessionId);
      // A fold with nothing in it at all is a session that was scheduled and never started, and the
      // header-only file it would otherwise produce is indistinguishable from a real report of a
      // session nobody registered for. Refuse it rather than hand the admin a plausible empty file.
      // The narrow reading is deliberate: a session that started and drew nobody, and one whose
      // players were all struck off, both still export the header alone, which is the honest answer
      // for a day that happened (docs/reporting.md).
      if (sessionUntouched(state)) {
        this.logger.warn(`report: exporting ${sessionId} refused, the aggregate is untouched`);
        this.showError(NEVER_RUN);
        return undefined;
      }
      return {
        filename: csvFilename(this.nameOf(sessionId), sessionId),
        csv: buildSessionCsv(state),
      };
    } catch (cause) {
      const failure =
        cause instanceof DomainError
          ? cause
          : new ServerError('The session could not be read.', { cause });
      // Written down where a human can find it, as well as put on the screen: the export is the
      // last thing this app does for a grading day and a failed one has no second chance.
      this.logger.warn(`report: exporting ${sessionId} failed`, failure.tag);
      this.showError(reportMessage(failure));
      return undefined;
    } finally {
      this.endExport();
    }
  };

  // What the Export button on the admin screen calls: build the file, then hand it to the browser.
  // The view stays one `void store.downloadSession(id)` and holds no `document` edge of its own.
  downloadSession = async (sessionId: string): Promise<void> => {
    const exported = await this.exportSession(sessionId);
    // Undefined means the export failed or was refused, and `lastError` already says which. There
    // is nothing to save and nothing more to tell anyone.
    if (exported === undefined) return;
    this.download?.save(exported.filename, exported.csv, CSV_MIME);
  };

  // The name the run list shows, which is the name the file gets. A session the fold has never
  // heard of still exports — the aggregate is the source of the rows, not the run list — and it is
  // named after the id alone, which the filename rule already carries.
  private nameOf(sessionId: string): string {
    return this.sessions.find((s) => s.sessionId === sessionId)?.name ?? 'session';
  }

  // Starting an export is also what clears the last failure: the message belonged to the previous
  // attempt, and leaving it up next to a file that downloaded fine is a message that gets believed.
  private startExport(): void {
    this._busy = true;
    this._lastError = undefined;
    this._refusal = undefined;
  }

  // The refusal dies with the export it refused for. That export produced a file, so the line
  // saying it did not is the message the store's own comment above warns about.
  private endExport(): void {
    this._busy = false;
    this._refusal = undefined;
  }

  private showError(message: string): void {
    this._lastError = message;
  }
}

// What the admin is told, derived from the failure CLASS and not from the message the gateway
// carried up. Every sentence ends in the next thing to try, and the ones with nothing to try say so
// rather than inviting a click that will fail identically.
function reportMessage(failure: DomainError): string {
  // Two different situations that arrive with the same words on them. Waiting for the wifi is
  // something the admin can do; waiting for a gateway that is not answering is not the same
  // instruction, and telling them apart is worth a sentence.
  if (failure instanceof OfflineError) {
    return 'This device is offline. Reconnect and export again.';
  }
  // Scheduled but never created: the aggregate is written by the session's first event, and a
  // session nobody ever started has none (docs/sync.md). The shipped gateway does not reject this
  // way — the SDK folds a missing aggregate to the empty state, which `exportSession` catches with
  // `sessionUntouched` and answers with the same sentence — but a gateway that does reject is
  // saying the same thing and is told the same thing.
  if (failure instanceof NotFoundError) {
    return NEVER_RUN;
  }
  // Reading a session needs a grant, and the admin laptop's grant is ownership. A second laptop, or
  // one whose storage was cleared, will fail this way forever.
  if (failure instanceof ForbiddenError) {
    return 'This device cannot read that session. Use the laptop that ran the grading day.';
  }
  if (failure instanceof UpdateRequiredError) {
    return 'This page is too old to read that session. Reload the page and export again.';
  }
  return 'The session could not be read. Check the connection and try the export again.';
}

export const TOKEN_ReportStore = token<ReportStore>('ReportStore');
